import { useEffect, useRef, useState } from "react";
import { Bell, Phone, CalendarCheck, MessageSquare, Sparkles, CreditCard, Megaphone, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CAT_ICON = {
  phone: Phone, booking: CalendarCheck, message: MessageSquare, ai: Sparkles,
  payment: CreditCard, lead: UserPlus, system: Megaphone,
};

export default function NotificationBell({ onOpenCentre }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const lastUnread = useRef(0);

  const load = () => api.get("/notifications", { params: { limit: 8 } }).then((r) => {
    setItems(r.data.notifications || []);
    const u = r.data.unread || 0;
    const newest = r.data.notifications?.[0];
    if (u > lastUnread.current && lastUnread.current !== 0 && newest) {
      if (newest.type === "payment_success") {
        toast.success("✅ Payment received — Booking confirmed", { description: newest.body });
        window.dispatchEvent(new Event("payment-updated"));
      } else if (newest.type === "payment_failed" || newest.type === "payment_refunded") {
        toast.error(newest.title, { description: newest.body });
        window.dispatchEvent(new Event("payment-updated"));
      } else {
        toast.info(newest.title, { description: newest.body });
      }
    }
    lastUnread.current = u; setUnread(u);
  }).catch(() => {});

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    const onRefresh = () => load();
    window.addEventListener("notif-refresh", onRefresh);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(iv); window.removeEventListener("notif-refresh", onRefresh); document.removeEventListener("mousedown", onClick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={() => setOpen((o) => !o)}
        className="relative h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 shadow-sm transition-colors">
        <Bell className="h-5 w-5" />
        {unread > 0 && <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="font-semibold text-sm">Notifications</span>
            {unread > 0 && <span className="text-xs text-slate-400">{unread} unread</span>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No notifications yet.</p>}
            {items.map((n) => {
              const Icon = CAT_ICON[n.category] || Bell;
              return (
                <div key={n.id} onClick={() => markRead(n.id)} className={`flex gap-2.5 px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 ${!n.read ? "bg-blue-50/40" : ""}`}>
                  <Icon className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{n.title}</p>
                    {n.body && <p className="text-xs text-slate-500 line-clamp-2">{n.body}</p>}
                  </div>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-blue-600 shrink-0 mt-1.5 ml-auto" />}
                </div>
              );
            })}
          </div>
          <button data-testid="notif-view-all" onClick={() => { setOpen(false); onOpenCentre(); }} className="w-full py-2.5 text-sm font-semibold text-blue-600 hover:bg-slate-50 border-t border-slate-100">View all</button>
        </div>
      )}
    </div>
  );
}
